import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useAdminGuard } from "../hooks/useAdminGuard";
import { useBrowserWallet } from "@nullaone/wallet-react";
import { BrandButton } from "@/components/brand/BrandButton";
import { BrandCard } from "@/components/brand/BrandCard";
import { BrandSpinner } from "@/components/brand/BrandLoading";
import { Shield, LogOut } from "lucide-react";

interface AdminRouteProps {
  children: React.ReactNode;
}

/**
 * AdminRoute — gates admin pages behind a wallet KV role.
 *
 * 2026-06-12 admin-auth alignment: no password / admin token. Access is
 * granted by an `operator`/`admin` role assigned to the connected wallet
 * (bootstrap via `bun scripts/seed-role.ts`; manage via the users panel).
 * States: no wallet → connect prompt; resolving → spinner; insufficient
 * role → access-denied card; operator/admin → render children.
 */
export function AdminRoute({ children }: AdminRouteProps) {
  const { t } = useTranslation();
  const { publicKey } = useBrowserWallet();
  const { hasAccess, isLoading } = useAdminGuard();

  if (hasAccess) {
    return <>{children}</>;
  }

  return (
    <div className="flex items-center justify-center min-h-[70vh] px-4">
      <BrandCard className="max-w-md w-full p-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-4">
          <Shield className="w-8 h-8 text-primary-blue" />
        </div>
        <h2 className="text-2xl font-bold text-text-dark">
          {t("admin.access.title")}
        </h2>

        {!publicKey ? (
          <p className="text-sm text-text-light mt-2">
            {t("admin.access.connectPrompt")}
          </p>
        ) : isLoading ? (
          <div className="mt-6 flex justify-center">
            <BrandSpinner size="md" />
          </div>
        ) : (
          <p className="text-sm text-text-light mt-2">
            {t("admin.access.denied")}
          </p>
        )}
      </BrandCard>
    </div>
  );
}

/**
 * Exit-admin button for the AdminLayout header. With no admin token to
 * clear, "logout" just leaves the admin area — wallet connection is managed
 * globally via the header wallet menu.
 */
export function AdminLogoutButton() {
  const { t } = useTranslation();
  return (
    <Link to="/">
      <BrandButton variant="ghost" size="sm" className="gap-2">
        <LogOut className="w-4 h-4" />
        {t("admin.exit")}
      </BrandButton>
    </Link>
  );
}
